import { z } from 'zod';
/* -------------------------------------------------------------------------- */
/* Cameras                                                                    */
/* -------------------------------------------------------------------------- */
export const CAMERA_STATUSES = [
    'CONNECTING',
    'ONLINE',
    'DEGRADED',
    'OFFLINE',
    'ERROR',
    'DISABLED',
];
export const CameraStatus = z.enum(CAMERA_STATUSES);
/** Statuses that mean "this camera is not currently producing usable frames". */
export const UNHEALTHY_CAMERA_STATUSES = ['OFFLINE', 'ERROR'];
export const ZONE_KINDS = [
    'ENTRY_EXIT',
    'DINING',
    'GAMING',
    'KITCHEN',
    'BILLING',
    'STORAGE',
    'OTHER',
];
export const ZoneKind = z.enum(ZONE_KINDS);
/** Zones where any presence outside an explicit allowance is notable. */
export const RESTRICTED_ZONE_KINDS = ['STORAGE', 'BILLING'];
/* -------------------------------------------------------------------------- */
/* Events & alerts                                                            */
/* -------------------------------------------------------------------------- */
export const EVENT_TYPES = [
    'PERSON_ENTERED',
    'PERSON_EXITED',
    'ZONE_OCCUPANCY_CHANGED',
    'LINE_CROSSED',
    'RESTRICTED_AREA_ENTRY',
    'AFTER_HOURS_MOVEMENT',
    'CROWD_THRESHOLD',
    'QUEUE_THRESHOLD',
    'STORAGE_ACTIVITY',
    'CAMERA_OFFLINE',
    'CAMERA_RECOVERED',
    'SERVICE_FAILURE',
    'ALERT_STORM',
    'CASHIER_ABSENT',
    'CUSTOMER_WAIT_EXCEEDED',
];
export const EventType = z.enum(EVENT_TYPES);
export const SEVERITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
export const Severity = z.enum(SEVERITIES);
export const SEVERITY_RANK = {
    LOW: 10,
    MEDIUM: 20,
    HIGH: 30,
    CRITICAL: 40,
};
export const EVENT_STATUSES = ['NEW', 'PROCESSED', 'SUPPRESSED'];
export const EventStatus = z.enum(EVENT_STATUSES);
export const ALERT_STATUSES = ['NEW', 'ACKNOWLEDGED', 'RESOLVED', 'FAILED'];
export const AlertStatus = z.enum(ALERT_STATUSES);
/**
 * Data provenance. `TEST` rows come from the development test-stream harness and
 * are rejected outright by the database when APP_ENV=production, so synthetic
 * data can never quietly contaminate real analytics.
 */
export const EVENT_SOURCES = ['VISION', 'SYSTEM', 'MANUAL', 'TEST'];
export const EventSource = z.enum(EVENT_SOURCES);
/* -------------------------------------------------------------------------- */
/* Notifications                                                              */
/* -------------------------------------------------------------------------- */
export const NOTIFICATION_CHANNELS = ['DASHBOARD', 'WEBPUSH', 'EMAIL', 'WHATSAPP'];
export const NotificationChannel = z.enum(NOTIFICATION_CHANNELS);
export const DELIVERY_STATUSES = ['PENDING', 'SENT', 'DELIVERED', 'FAILED', 'SKIPPED'];
export const DeliveryStatus = z.enum(DELIVERY_STATUSES);
/* -------------------------------------------------------------------------- */
/* Identity & access                                                          */
/* -------------------------------------------------------------------------- */
export const ROLES = ['OWNER', 'ADMIN', 'MANAGER', 'SUPERVISOR', 'VIEWER'];
export const Role = z.enum(ROLES);
export const PERMISSIONS = [
    'camera:read',
    'camera:write',
    'camera:stream',
    'zone:read',
    'zone:write',
    'event:read',
    'alert:read',
    'alert:ack',
    'alert:resolve',
    'analytics:read',
    'user:read',
    'user:manage',
    'settings:read',
    'settings:manage',
    'system:read',
    'audit:read',
];
export const Permission = z.enum(PERMISSIONS);
/**
 * Role -> permission map. Authorisation is evaluated server-side only; the UI
 * uses this same table purely to decide what to render, never to decide access.
 */
export const ROLE_PERMISSIONS = {
    OWNER: [...PERMISSIONS],
    ADMIN: [
        'camera:read', 'camera:write', 'camera:stream',
        'zone:read', 'zone:write',
        'event:read', 'alert:read', 'alert:ack', 'alert:resolve',
        'analytics:read',
        'user:read', 'user:manage',
        'settings:read', 'settings:manage',
        'system:read', 'audit:read',
    ],
    MANAGER: [
        'camera:read', 'camera:stream',
        'zone:read',
        'event:read', 'alert:read', 'alert:ack', 'alert:resolve',
        'analytics:read',
        'user:read',
        'settings:read',
        'system:read',
    ],
    SUPERVISOR: [
        'camera:read', 'camera:stream',
        'zone:read',
        'event:read', 'alert:read', 'alert:ack',
        'analytics:read',
        'system:read',
    ],
    VIEWER: [
        'camera:read',
        'zone:read',
        'event:read', 'alert:read',
    ],
};
/**
 * Zone kinds a role may see at all. A floor supervisor has no operational reason
 * to watch the billing counter or the storage room, so those cameras, their live
 * streams and their events are filtered out server-side before they are ever
 * serialised.
 */
export const ROLE_VISIBLE_ZONE_KINDS = {
    OWNER: 'ALL',
    ADMIN: 'ALL',
    MANAGER: 'ALL',
    SUPERVISOR: ['ENTRY_EXIT', 'DINING', 'GAMING', 'OTHER'],
    VIEWER: ['DINING', 'GAMING'],
};
export function permissionsForRole(role) {
    return ROLE_PERMISSIONS[role];
}
export function roleCan(role, permission) {
    return ROLE_PERMISSIONS[role].includes(permission);
}
export function roleSeesZoneKind(role, kind) {
    const visible = ROLE_VISIBLE_ZONE_KINDS[role];
    return visible === 'ALL' || visible.includes(kind);
}
//# sourceMappingURL=enums.js.map